// src/contexts/ChannelsProvider.tsx
//
// Page-level provider that owns ChannelsPage's table state (filter, sort,
// search, pagination) AND re-exposes channel/device-group data from
// ChannelsDataProvider so existing useChannels() consumers don't change
// their imports. The actual data fetch lives in ChannelsDataProvider —
// see ../contexts/ChannelsDataProvider.tsx.

import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Pagination } from 'lib/types/tableTools'
import { TableState } from 'lib/types'
import * as utils from '../components/partial-pages/ChannelsPage/utils'
import { Channel } from 'lib/types/channels'
import { ChannelsColumnKeys } from 'lib/constants/keys/channels'
import type { DeviceGroup } from 'lib/types/deviceGroups'
import { useChannelsData } from './ChannelsDataProvider'

type ChannelsContextType = {
  channels: Channel[]
  deviceGroups: DeviceGroup[]

  loading: boolean
  loadingChannels: boolean
  loadingDeviceGroups: boolean
  channelsLoaded: boolean
  channelsOrgId: string | null

  tableState: TableState<string, ChannelsColumnKeys>
  pagination: Pagination

  setChannels: React.Dispatch<React.SetStateAction<Channel[]>>
  setDeviceGroups: React.Dispatch<React.SetStateAction<DeviceGroup[]>>

  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setTableState: React.Dispatch<React.SetStateAction<TableState<string, ChannelsColumnKeys>>>
  setPagination: React.Dispatch<React.SetStateAction<Pagination>>

  refreshChannels: () => Promise<void>
  refreshDeviceGroups: () => Promise<void>
  refreshAll: () => Promise<void>

  upsertDeviceGroupOptimistic: (payload: any) => Promise<DeviceGroup>
}

const ChannelsContext = createContext<ChannelsContextType | null>(null)

type ChannelsProviderProps = { children: ReactNode }

export const useChannels = () => {
  const ctx = useContext(ChannelsContext)
  if (!ctx) throw new Error('useChannels must be used within a ChannelsProvider')
  return ctx
}

export const ChannelsProvider: React.FC<ChannelsProviderProps> = ({ children }) => {
  const data = useChannelsData()

  // Local UI-only state (table filters, pagination) that doesn't belong in
  // the global data provider.
  const [loading, setLoading] = useState(false)
  const [tableState, setTableState] = useState<TableState<string, ChannelsColumnKeys>>({
    filterBy: utils.getFilterOptions()[0],
    sortBy: utils.getSortOptions()[0].sortBy,
    searchBy: '',
  })
  const [pagination, setPagination] = useState<Pagination>({ currentPage: null })

  return (
    <ChannelsContext.Provider
      value={{
        channels: data.channels,
        deviceGroups: data.deviceGroups,

        loading: loading || data.loading,
        loadingChannels: data.loadingChannels,
        loadingDeviceGroups: data.loadingDeviceGroups,
        channelsLoaded: data.channelsLoaded,
        channelsOrgId: data.channelsOrgId,

        setChannels: data.setChannels,
        setDeviceGroups: data.setDeviceGroups,

        setLoading,
        setTableState,
        setPagination,

        tableState,
        pagination,

        refreshChannels: data.refreshChannels,
        refreshDeviceGroups: data.refreshDeviceGroups,
        refreshAll: data.refreshAll,

        upsertDeviceGroupOptimistic: data.upsertDeviceGroupOptimistic,
      }}
    >
      {children}
    </ChannelsContext.Provider>
  )
}
